/**
 * ChatErrorBanner — persistent error banner with a Retry button.
 *
 * PHASE_D_GAP_QA_BRIEFING.md §"Frontend quality fixes / Fix 1".
 *
 * Renders when chatStore.error is set. Shows a Retry button when there is
 * a lastUserMessage to resend AND the error is not auth-related (retrying
 * a 401 is pointless — the user needs to re-authenticate).
 *
 * Pattern mirrors NoteUpdatedBanner (border + icon + text body) but uses
 * the warning/error tokens.
 */
import { AlertTriangle, RotateCw } from 'react-feather';

import { useChatStore } from '../../../stores/chatStore';
import { useChatStream } from '../hooks/useChatStream';

const AUTH_ERROR_HINTS = ['auth', 'sign in', 'session', '401', '403'];

function isAuthError(message: string): boolean {
  const lower = message.toLowerCase();
  return AUTH_ERROR_HINTS.some((h) => lower.includes(h));
}

export function ChatErrorBanner() {
  const error = useChatStore((s) => s.error);
  const lastUserMessage = useChatStore((s) => s.lastUserMessage);
  const clearError = useChatStore((s) => s.clearError);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const { send } = useChatStream();

  if (!error) {return null;}
  const canRetry = !!lastUserMessage && !isAuthError(error) && !isStreaming;

  return (
    <div className="mx-4 mb-3 flex items-start gap-2 border border-error/40 bg-error/5 px-3 py-2 text-body-sm">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-error" strokeWidth={2.5} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-txt-primary">Something went wrong</p>
        <p className="mt-1 text-txt-secondary">{error}</p>
        {canRetry && (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const text = lastUserMessage;
                clearError();
                if (text) {send({ text });}
              }}
              className="inline-flex items-center gap-1 border border-orange bg-surface-card px-3 py-1 text-body-sm font-semibold text-orange transition-colors hover:bg-orange hover:text-surface-card"
            >
              <RotateCw size={12} />
              Retry
            </button>
            <button
              type="button"
              onClick={clearError}
              className="text-body-sm text-txt-tertiary hover:text-txt-primary"
            >
              Dismiss
            </button>
          </div>
        )}
        {!canRetry && (
          <button
            type="button"
            onClick={clearError}
            className="mt-2 text-body-sm text-txt-tertiary hover:text-txt-primary"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}
